import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { MagicBackground } from '../components/MagicBackground';

interface MenuItem {
  itemId: string;
  name: string;
  description?: string;
  price: number; // in cents
  category?: string;
  isAvailable: boolean;
  taxable: boolean;
  taxRate?: number;
}

interface TaxConfig {
  enabled: boolean;
  defaultTaxRate: number;
  taxIncluded: boolean;
  taxName: string;
}

export default function Menu() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>({
    enabled: false,
    defaultTaxRate: 0.08,
    taxIncluded: false,
    taxName: 'Sales Tax',
  });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [squareConnected, setSquareConnected] = useState(false);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    isAvailable: true,
    taxable: true,
    taxRate: '',
  });

  useEffect(() => {
    loadMenu();
    loadTaxConfig();
    checkSquareConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSquareConnection = async () => {
    try {
      const squareConfig = await apiService.getIntegration('square');
      setSquareConnected(squareConfig?.connected || false);
      if (squareConfig?.lastMenuSync) {
        setLastSync(squareConfig.lastMenuSync);
      }
    } catch (error) {
      console.error('Error checking Square connection:', error);
      setSquareConnected(false);
    }
  };

  const loadMenu = async () => {
    try {
      setLoading(true);
      const items = await apiService.getMenuItems();
      setMenuItems(items);
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTaxConfig = async () => {
    try {
      const config = await apiService.getTaxConfig();
      // Ensure all required fields are present, use defaults if missing
      if (config) {
        setTaxConfig({
          enabled: config.enabled ?? false,
          defaultTaxRate: config.defaultTaxRate ?? 0.08,
          taxIncluded: config.taxIncluded ?? false,
          taxName: config.taxName ?? 'Sales Tax',
        });
      }
      // If config is null/undefined, keep default values
    } catch (error) {
      console.error('Error loading tax config:', error);
      // Keep default values on error
    }
  };

  const handleOpenDialog = (item?: MenuItem) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        price: (item.price / 100).toFixed(2),
        category: item.category || '',
        isAvailable: item.isAvailable,
        taxable: item.taxable,
        taxRate: item.taxRate ? (item.taxRate * 100).toFixed(2) : '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        isAvailable: true,
        taxable: true,
        taxRate: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleSaveItem = async () => {
    try {
      // Send price in dollars - backend will convert to cents
      const itemData = {
        name: formData.name,
        description: formData.description || undefined,
        price: parseFloat(formData.price), // Send as dollars (e.g., 4.75)
        category: formData.category || undefined,
        isAvailable: formData.isAvailable,
        taxable: formData.taxable,
        taxRate: formData.taxRate ? parseFloat(formData.taxRate) / 100 : undefined,
      };

      if (editingItem) {
        await apiService.updateMenuItem(editingItem.itemId, itemData);
      } else {
        await apiService.createMenuItem(itemData);
      }
      handleCloseDialog();
      loadMenu();
    } catch (error) {
      console.error('Error saving menu item:', error);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      try {
        await apiService.deleteMenuItem(itemId);
        loadMenu();
      } catch (error) {
        console.error('Error deleting menu item:', error);
      }
    }
  };

  const handleSaveTaxConfig = async () => {
    try {
      await apiService.updateTaxConfig(taxConfig);
      loadTaxConfig();
    } catch (error) {
      console.error('Error saving tax config:', error);
    }
  };

  const handleImportFromSquare = async (showConfirm = true) => {
    if (showConfirm && !window.confirm('This will sync all menu items from your Square catalog. Existing items with the same name will be updated. Continue?')) {
      return;
    }

    try {
      setImporting(true);
      setSyncMessage('');
      const result = await apiService.importMenuFromSquare();
      const message = `Synced ${result.total} items: ${result.imported} new, ${result.updated} updated`;
      setSyncMessage(message);
      setLastSync(result.lastSync);
      loadMenu();
      checkSquareConnection();
      
      // Clear message after 5 seconds
      setTimeout(() => setSyncMessage(''), 5000);
    } catch (error: any) {
      console.error('Error importing from Square:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error?.message || 'Failed to sync from Square. Make sure Square is connected in Settings.';
      setSyncMessage(`Error: ${errorMsg}`);
      setTimeout(() => setSyncMessage(''), 5000);
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
        }}
      >
        <MagicBackground />
        <CircularProgress sx={{ color: '#667eea', position: 'relative', zIndex: 1 }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 50%, #0a0e27 100%)',
      minHeight: '100vh', 
      position: 'relative',
      py: 4 
    }}>
      <MagicBackground />
      
      <Box maxWidth="xl" sx={{ mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Menu & Pricing
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.7)" mt={1}>
              Manage your products, services, and pricing for AI-powered orders
            </Typography>
          </Box>
          <Box display="flex" gap={2} alignItems="center">
            {squareConnected && (
              <>
                <Box display="flex" flexDirection="column" alignItems="flex-end" mr={1}>
                  {lastSync && (
                    <Typography variant="caption" color="rgba(255,255,255,0.6)">
                      Last synced: {new Date(lastSync).toLocaleString()}
                    </Typography>
                  )}
                  {syncMessage && (
                    <Typography 
                      variant="caption" 
                      color={syncMessage.startsWith('Error') ? '#ff6b6b' : '#51cf66'}
                      fontWeight={600}
                    >
                      {syncMessage}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={importing ? <CircularProgress size={16} sx={{ color: '#667eea' }} /> : <RefreshIcon />}
                  onClick={() => handleImportFromSquare(false)}
                  disabled={importing}
                  size="small"
                  sx={{
                    borderColor: 'rgba(102, 126, 234, 0.5)',
                    color: '#667eea',
                    '&:hover': { borderColor: '#667eea', bgcolor: 'rgba(102, 126, 234, 0.1)' },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={() => handleImportFromSquare(true)}
                  disabled={importing}
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    '&:hover': { borderColor: '#5568d3', bgcolor: 'rgba(102, 126, 234, 0.1)' },
                  }}
                >
                  {importing ? 'Syncing...' : 'Sync from Square'}
                </Button>
              </>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5568d3' },
              }}
            >
              Add Item
            </Button>
          </Box>
        </Box>

        {/* Tax Configuration */}
        <Card
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: taxConfig?.enabled
              ? '1px solid rgba(67, 233, 123, 0.3)'
              : '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            mb: 3,
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <RestaurantIcon sx={{ color: '#667eea' }} />
                <Typography variant="h6" color="white" fontWeight={600}>
                  Sales Tax
                </Typography>
                <Chip
                  label={taxConfig?.enabled ? 'Active' : 'Not collecting'}
                  size="small"
                  sx={{
                    bgcolor: taxConfig?.enabled ? 'rgba(67, 233, 123, 0.2)' : 'rgba(255,255,255,0.1)',
                    color: taxConfig?.enabled ? '#43e97b' : 'rgba(255,255,255,0.5)',
                    fontWeight: 600,
                  }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={taxConfig?.enabled || false}
                    onChange={(e) => setTaxConfig({ ...taxConfig, enabled: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#43e97b' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#43e97b' },
                    }}
                  />
                }
                label={<Typography color="white" variant="body2">Collect tax on orders</Typography>}
                sx={{ m: 0 }}
              />
            </Box>

            {!taxConfig?.enabled && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 2, fontSize: '0.8rem' }}>
                Tax collection is off. Enable above to add tax to customer orders.
              </Typography>
            )}

            <Box display="flex" gap={3} flexWrap="wrap" sx={{ opacity: taxConfig?.enabled ? 1 : 0.4, pointerEvents: taxConfig?.enabled ? 'auto' : 'none' }}>
              <TextField
                label="Tax Name"
                value={taxConfig?.taxName || 'Sales Tax'}
                onChange={(e) => setTaxConfig({ ...taxConfig, taxName: e.target.value })}
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <TextField
                label="Tax Rate (%)"
                type="number"
                value={((taxConfig?.defaultTaxRate || 0) * 100).toFixed(2)}
                onChange={(e) => setTaxConfig({ ...taxConfig, defaultTaxRate: parseFloat(e.target.value) / 100 || 0 })}
                size="small"
                sx={{
                  width: 150,
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={taxConfig?.taxIncluded || false}
                    onChange={(e) => setTaxConfig({ ...taxConfig, taxIncluded: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' },
                    }}
                  />
                }
                label="Tax already included in prices"
                sx={{ color: 'white' }}
              />
            </Box>
            <Box mt={2}>
              <Button
                variant="contained"
                onClick={handleSaveTaxConfig}
                sx={{
                  bgcolor: '#667eea',
                  '&:hover': { bgcolor: '#5568d3' },
                }}
              >
                Save Tax Settings
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Menu Items Table */}
        <TableContainer
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Price</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Taxable</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Available</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {menuItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="rgba(255,255,255,0.7)">
                      No menu items yet. Click "Add Item" to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                menuItems.map((item) => (
                  <TableRow key={item.itemId}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{item.name}</TableCell>
                    <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{item.description || '-'}</TableCell>
                    <TableCell>
                      {item.category && (
                        <Chip
                          label={item.category}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(102, 126, 234, 0.2)',
                            color: '#667eea',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>${(item.price / 100).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.taxable ? 'Yes' : 'No'}
                        size="small"
                        color={item.taxable ? 'success' : 'default'}
                        sx={{
                          bgcolor: item.taxable ? 'rgba(67, 233, 123, 0.2)' : 'rgba(255,255,255,0.1)',
                          color: item.taxable ? '#43e97b' : 'rgba(255,255,255,0.7)',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.isAvailable ? 'Available' : 'Unavailable'}
                        size="small"
                        color={item.isAvailable ? 'success' : 'error'}
                        sx={{
                          bgcolor: item.isAvailable ? 'rgba(67, 233, 123, 0.2)' : 'rgba(255,255,255,0.1)',
                          color: item.isAvailable ? '#43e97b' : 'rgba(255,255,255,0.7)',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item.itemId)}
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#1a1f3a',
              color: 'white',
            },
          }}
        >
          <DialogTitle>
            {editingItem ? 'Edit Menu Item' : 'New Menu Item'}
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} mt={2}>
              <TextField
                label="Name"
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                }}
              />
              <Box display="flex" gap={2}>
                <TextField
                  label="Price ($)"
                  type="number"
                  fullWidth
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
                <TextField
                  label="Category"
                  fullWidth
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
              </Box>
              <Box display="flex" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isAvailable}
                      onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' },
                      }}
                    />
                  }
                  label="Available"
                  sx={{ color: 'white' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.taxable}
                      onChange={(e) => setFormData({ ...formData, taxable: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#667eea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#667eea' },
                      }}
                    />
                  }
                  label="Taxable"
                  sx={{ color: 'white' }}
                />
              </Box>
              {formData.taxable && (
                <TextField
                  label="Custom Tax Rate (%)"
                  type="number"
                  fullWidth
                  value={formData.taxRate}
                  onChange={(e) => setFormData({ ...formData, taxRate: e.target.value })}
                  helperText="Leave empty to use default tax rate"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
                  }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveItem}
              variant="contained"
              disabled={!formData.name || !formData.price}
              sx={{
                bgcolor: '#667eea',
                '&:hover': { bgcolor: '#5568d3' },
              }}
            >
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}


